
import { Link } from "react-router-dom";
import { EMPTY_ARRAY, useAppStore } from "@/lib/store";
import ProductCard from "@/components/product/ProductCard";
import RevealOnScroll from "@/components/ui/RevealOnScroll";
import ImmersiveBackground from "@/components/ui/ImmersiveBackground";
import { useDocumentMeta } from "@/lib/useDocumentMeta";

export default function FavouritesPage() {
  useDocumentMeta({
    title: "Favourites",
    description: "Your saved favourites on Nerd Multiverse.",
    path: "/favourites",
    noindex: true,
  });

  const authReady = useAppStore((s) => s._authReady);
  const user = useAppStore((s) => s.currentUser());
  const products = useAppStore((s) => s.products);
  const favourites = useAppStore((s) =>
    user ? s.favourites[user.uid] ?? EMPTY_ARRAY : EMPTY_ARRAY
  );

  const items = products.filter((p) => favourites.includes(p.id));

  // Firebase Auth resolves asynchronously, even on a hard page load where
  // the user is already signed in — without this, a genuinely logged-in
  // user briefly sees "Sign in to save favourites" before it corrects
  // itself. RequireRole (seller/admin dashboards) already guards for this;
  // this page didn't.
  if (!authReady) return null;

  return (
    <div className="relative pt-28 md:pt-36 pb-24 min-h-dvh">
      <ImmersiveBackground
        src="/images/hero/favourites-v2.webp"
        mobileSrc="/images/hero/favourites-mobile-v2.webp"
        focus="50% 28%"
      />
      <div className="nm-container">
        <RevealOnScroll>
          <h1 className="font-display text-4xl font-semibold text-white">
            Favourites
          </h1>
        </RevealOnScroll>

        {!user ? (
          <div className="mt-16 text-center">
            <p className="font-display text-2xl text-white">Sign in to save favourites.</p>
            <Link
              to="/login"
              className="mt-8 inline-block px-7 py-3.5 rounded-full bg-white text-[#05070d] text-sm font-medium tracking-wide hover:bg-[var(--nm-blue-bright)] hover:text-white transition-colors"
            >
              SIGN IN
            </Link>
          </div>
        ) : items.length === 0 ? (
          <div className="mt-16 text-center">
            <p className="font-display text-2xl text-white">No treasures here yet.</p>
            <p className="mt-2 text-[var(--nm-ink-muted)]">
              Your next find could be waiting somewhere else in the Multiverse.
            </p>
            <Link
              to="/marketplace"
              className="mt-8 inline-block px-7 py-3.5 rounded-full bg-white text-[#05070d] text-sm font-medium tracking-wide hover:bg-[var(--nm-blue-bright)] hover:text-white transition-colors"
            >
              EXPLORE THE MARKETPLACE
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {items.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
